import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderChangeObjectData } from 'gql/graphql';

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerWorkOrderChangeObjectsForRemindersGql } from '../../WorkOrderChangeQueries';

export interface IMoveVolumesBetweenSeasonsForObjectItem {
    id: string | null;
    name: string;
    volume: number | null;
    parentId: string | null;
    remainder: number;
    workOrderObjectId: number | null;
    workOrderObjectChangeId: number | null;
}

interface IUseUerWorkOrderChangeObjectsForReminders {
    isOpen: boolean;
    workOrderChangeId: number;
    workOrderChangeRootObjectId?: number;
    workOrderRootObjectId?: number;
}

const requestId = 'uerWorkOrderChangeObjectsForReminders';

const getTupleId = (item?: WorkOrderChangeObjectData | null) =>
    item ? `${item?.id || 0}_${item?.workOrderObjectId}` : null;

export const useUerWorkOrderChangeObjectsForReminders = (props: IUseUerWorkOrderChangeObjectsForReminders) => {
    const dispatch = useDispatch();
    const { workOrderChangeId, workOrderRootObjectId, workOrderChangeRootObjectId, isOpen } = props;

    const [uerWorkOrderChangeObjectsForRemindersReset] = useResetStoreData({ requestId });

    const [
        uerWorkOrderChangeObjectsForRemindersData,
        uerWorkOrderChangeObjectsForRemindersStatus,
        ,
        uerWorkOrderChangeObjectsForRemindersRequest,
    ] = useRequest({
        requestId,
        query: uerWorkOrderChangeObjectsForRemindersGql,
        withEffect: false,
    });

    const items: IMoveVolumesBetweenSeasonsForObjectItem[] = useMemo(() => {
        const itemsToMap = uerWorkOrderChangeObjectsForRemindersData?.uerWorkOrderChangeObjectsForReminders?.data || [];

        return itemsToMap.map((item: WorkOrderChangeObjectData | null) => ({
            id: getTupleId(item),
            name: item?.name || '',
            volume: null,
            parentId: getTupleId(
                itemsToMap.find((i) => i?.id === item?.parentId && i?.workOrderObjectId === item?.workOrderParentId),
            ),
            remainder: 100,
            workOrderObjectId: item?.workOrderObjectId,
            workOrderObjectChangeId: item?.id,
        }));
    }, [uerWorkOrderChangeObjectsForRemindersData?.uerWorkOrderChangeObjectsForReminders?.data]);

    useEffect(() => {
        if (isOpen && workOrderChangeId && (workOrderChangeRootObjectId || workOrderRootObjectId)) {
            dispatch(
                uerWorkOrderChangeObjectsForRemindersRequest({
                    workOrderChangeId,
                    workOrderChangeRootObjectId,
                    workOrderRootObjectId,
                }),
            );
        } else {
            uerWorkOrderChangeObjectsForRemindersReset(undefined);
        }
    }, [
        dispatch,
        isOpen,
        uerWorkOrderChangeObjectsForRemindersRequest,
        uerWorkOrderChangeObjectsForRemindersReset,
        workOrderChangeId,
        workOrderChangeRootObjectId,
        workOrderRootObjectId,
    ]);

    return {
        status: uerWorkOrderChangeObjectsForRemindersStatus,
        items,
    };
};
